import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { User, UpdateCryptoBalanceData } from "../../types";
import {
  MdAccountBalanceWallet,
  MdSearch,
  MdRefresh,
  MdEdit,
  MdCurrencyBitcoin,
} from "react-icons/md";

interface CryptoBalance {
  balance: number;
  price: number;
  usd_value: number;
}

interface UserCryptoBalances {
  user: {
    id: number;
    name: string;
    email: string;
  };
  balances: {
    [key: string]: CryptoBalance;
  };
  total_crypto_value: number;
}

const CryptoBalanceManagement = () => {
  const { addAlert } = useAlert();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userBalances, setUserBalances] = useState<UserCryptoBalances | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cryptoPrices, setCryptoPrices] = useState<any>({});
  const [editModal, setEditModal] = useState({
    isOpen: false,
    coin: "",
    currentBalance: 0,
    operation: "set" as "set" | "add" | "subtract",
    amount: "",
  });

  const WalletIcon = MdAccountBalanceWallet as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const EditIcon = MdEdit as React.FC<React.SVGProps<SVGSVGElement>>;
  const CryptoIcon = MdCurrencyBitcoin as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  // Updated coins array to include ZORA and DOGE
  const coins = [
    "BTC",
    "ETH",
    "USDT",
    "SOL",
    "XRP",
    "BNB",
    "XLM",
    "ZORA",
    "DOGE",
  ];

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch users" });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCryptoPrices = async () => {
    try {
      const response = await adminAPI.getCryptoPrices();
      setCryptoPrices(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch crypto prices" });
      // Set fallback prices
      setCryptoPrices({
        bitcoin: { usd: 45000 },
        ethereum: { usd: 2500 },
        tether: { usd: 1 },
        solana: { usd: 100 },
        ripple: { usd: 0.6 },
        binancecoin: { usd: 350 },
        stellar: { usd: 0.12 },
        zora: { usd: 0.5 },
        dogecoin: { usd: 0.08 },
      });
    }
  };

  const fetchUserBalances = async (userId: number) => {
    try {
      setIsLoadingBalances(true);
      const response = await adminAPI.getUserCryptoBalances(userId);
      setUserBalances(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch user balances" });
    } finally {
      setIsLoadingBalances(false);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    fetchUserBalances(user.id);
  };

  const handleUpdateBalance = async () => {
    if (!selectedUser || !editModal.amount) return;

    try {
      const updateData: UpdateCryptoBalanceData = {
        user_id: selectedUser.id,
        coin: editModal.coin,
        amount: parseFloat(editModal.amount),
        operation: editModal.operation,
      };

      await adminAPI.updateUserCryptoBalance(updateData);

      addAlert({
        type: "success",
        message: "Crypto balance updated successfully",
      });
      setEditModal({ ...editModal, isOpen: false, amount: "" });
      fetchUserBalances(selectedUser.id);
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to update balance",
      });
    }
  };

  const openEditModal = (coin: string, currentBalance: number) => {
    setEditModal({
      isOpen: true,
      coin,
      currentBalance,
      operation: "set",
      amount: currentBalance.toString(),
    });
  };

  const getCoinGeckoId = (coin: string) => {
    const mapping: { [key: string]: string } = {
      BTC: "bitcoin",
      ETH: "ethereum",
      USDT: "tether",
      SOL: "solana",
      XRP: "ripple",
      BNB: "binancecoin",
      XLM: "stellar",
      ZORA: "zora",
      DOGE: "dogecoin",
    };
    return mapping[coin] || "";
  };

  const getCoinColor = (coin: string) => {
    const colors: { [key: string]: string } = {
      BTC: "bg-orange-500",
      ETH: "bg-gray-500",
      USDT: "bg-green-500",
      SOL: "bg-purple-500",
      XRP: "bg-blue-500",
      BNB: "bg-yellow-500",
      XLM: "bg-pink-500",
      ZORA: "bg-indigo-500",
      DOGE: "bg-amber-500",
    };
    return colors[coin] || "bg-primary";
  };

  useEffect(() => {
    fetchUsers();
    fetchCryptoPrices();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Crypto Balance Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Manage user cryptocurrency balances and track portfolio values.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchCryptoPrices}
            className={`${GlobalStyles.buttonIconOutline}`}
            title="Refresh Prices"
          >
            <RefreshIcon className="w-4 h-4" />
          </button>
          <button
            onClick={fetchUsers}
            className={`${GlobalStyles.buttonIconOutline}`}
            title="Refresh Users"
          >
            <RefreshIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Crypto Prices Overview */}
      <div className={`${GlobalStyles.sectionWrap} mb-6`}>
        <h2
          className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
        >
          Current Crypto Prices
        </h2>
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4">
          {coins.map((coin) => {
            const coinId = getCoinGeckoId(coin);
            const price = cryptoPrices[coinId]?.usd;
            return (
              <div
                key={coin}
                className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div
                  className={`font-bold ${getCoinColor(coin)} text-white text-xs px-2 py-1 rounded-full inline-block mb-2`}
                >
                  {coin}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {price ? (
                    <>
                      $
                      {price.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: coin === "USDT" ? 2 : 6,
                      })}
                    </>
                  ) : (
                    <span className="text-gray-400">Loading...</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Users List */}
        <div className="lg:col-span-1">
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h3
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              Users
            </h3>

            <div className={GlobalStyles.inputWrapper}>
              <SearchIcon className={GlobalStyles.inputIcon} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={GlobalStyles.input}
              />
            </div>

            <div className="mt-4 space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No users found
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedUser?.id === user.id
                        ? "bg-gradient-to-r from-primary to-purple-600 text-white shadow-md"
                        : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                    }`}
                  >
                    <div className="font-medium">
                      {user.first_name} {user.last_name}
                    </div>
                    <div
                      className={`text-sm ${
                        selectedUser?.id === user.id
                          ? "text-blue-100"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {user.email}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded">
                        Level {user.account_level}
                      </span>
                      <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded">
                        {user.role}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* User Crypto Balances */}
        <div className="lg:col-span-3">
          {selectedUser ? (
            <div className={`${GlobalStyles.sectionWrap}`}>
              <div className={`${CommonClasses.flexBetween} mb-6`}>
                <div>
                  <h2
                    className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
                  >
                    {selectedUser.first_name}'s Crypto Portfolio
                  </h2>
                  <p className={`${CommonClasses.textGray}`}>
                    {selectedUser.email}
                  </p>
                </div>
                {userBalances && (
                  <div className="text-right">
                    <div
                      className={`${CommonClasses.textXl} font-bold text-primary`}
                    >
                      $
                      {userBalances.total_crypto_value.toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </div>
                    <div
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      Total Crypto Value
                    </div>
                  </div>
                )}
              </div>

              {isLoadingBalances ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className={`${CommonClasses.textGray} mt-4`}>
                    Loading balances...
                  </p>
                </div>
              ) : userBalances ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {coins.map((coin) => {
                    const balance = userBalances.balances[coin] || {
                      balance: 0,
                      price: 0,
                      usd_value: 0,
                    };
                    return (
                      <div
                        key={coin}
                        className={`bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 transition-all duration-300 hover:shadow-md`}
                      >
                        <div className={`${CommonClasses.flexBetween} mb-4`}>
                          <div className={`${CommonClasses.flexStart} gap-3`}>
                            <div
                              className={`w-10 h-10 ${getCoinColor(coin)} rounded-full flex items-center justify-center`}
                            >
                              <CryptoIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800 dark:text-gray-200">
                                {coin}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {balance.balance.toFixed(8)}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => openEditModal(coin, balance.balance)}
                            className={`${GlobalStyles.buttonIconOutline} hover:scale-110 transition-transform`}
                            title={`Edit ${coin} balance`}
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="space-y-2">
                          <div className={`${CommonClasses.flexBetween}`}>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              USD Value:
                            </span>
                            <span className="font-semibold text-primary">
                              $
                              {balance.usd_value.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                          <div className={`${CommonClasses.flexBetween}`}>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Current Price:
                            </span>
                            <span className="font-semibold">
                              $
                              {balance.price.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: coin === "USDT" ? 2 : 6,
                              })}
                            </span>
                          </div>
                          <div className={`${CommonClasses.flexBetween}`}>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              24h Change:
                            </span>
                            <span
                              className={`text-sm font-medium ${
                                cryptoPrices[getCoinGeckoId(coin)]
                                  ?.usd_24h_change >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {cryptoPrices[getCoinGeckoId(coin)]
                                ?.usd_24h_change
                                ? `${cryptoPrices[getCoinGeckoId(coin)].usd_24h_change.toFixed(2)}%`
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <WalletIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className={`${CommonClasses.textGray}`}>
                    Select a user to view their crypto balances
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className={`${GlobalStyles.sectionWrap} text-center py-16`}>
              <WalletIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-600 dark:text-gray-300 mb-2">
                Select a User
              </h3>
              <p className={`${CommonClasses.textGray}`}>
                Choose a user from the list to view and manage their
                cryptocurrency balances
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Balance Modal */}
      {editModal.isOpen && (
        <div className={GlobalStyles.modalOverlay}>
          <div className={GlobalStyles.modalContainer}>
            <div className="p-5 border-b border-gray-300 dark:border-gray-700">
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
              >
                Update {editModal.coin} Balance
              </h2>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
              >
                For {selectedUser?.first_name} {selectedUser?.last_name}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Current Balance */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className={`${CommonClasses.flexBetween}`}>
                  <span className="text-gray-600 dark:text-gray-400">
                    Current Balance:
                  </span>
                  <span className="font-semibold">
                    {editModal.currentBalance.toFixed(8)} {editModal.coin}
                  </span>
                </div>
              </div>

              {/* Operation Selection */}
              <div>
                <label className={GlobalStyles.label}>Operation</label>
                <select
                  value={editModal.operation}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      operation: e.target.value as "set" | "add" | "subtract",
                    })
                  }
                  className={GlobalStyles.input}
                >
                  <option value="set">Set to specific amount</option>
                  <option value="add">Add amount</option>
                  <option value="subtract">Subtract amount</option>
                </select>
              </div>

              {/* Amount Input */}
              <div>
                <label className={GlobalStyles.label}>
                  {editModal.operation === "set"
                    ? "New Balance"
                    : editModal.operation === "add"
                      ? "Amount to Add"
                      : "Amount to Subtract"}{" "}
                  ({editModal.coin})
                </label>
                <input
                  type="number"
                  step="0.00000001"
                  min="0"
                  value={editModal.amount}
                  onChange={(e) =>
                    setEditModal({ ...editModal, amount: e.target.value })
                  }
                  className={GlobalStyles.input}
                  placeholder="0.00000000"
                />
              </div>

              {/* USD Equivalent */}
              {editModal.amount && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span className="text-sm text-blue-800 dark:text-blue-200">
                      USD Value:
                    </span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      $
                      {(
                        parseFloat(editModal.amount) *
                        (userBalances?.balances[editModal.coin]?.price || 0)
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* Preview */}
              {editModal.amount && editModal.operation !== "set" && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span className="text-sm text-green-800 dark:text-green-200">
                      New Balance:
                    </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {editModal.operation === "add"
                        ? (
                            editModal.currentBalance +
                            parseFloat(editModal.amount)
                          ).toFixed(8)
                        : Math.max(
                            0,
                            editModal.currentBalance -
                              parseFloat(editModal.amount),
                          ).toFixed(8)}{" "}
                      {editModal.coin}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-300 dark:border-gray-700 flex gap-2">
              <button
                onClick={() => setEditModal({ ...editModal, isOpen: false })}
                className={`${GlobalStyles.buttonOutline} flex-1`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateBalance}
                disabled={!editModal.amount || parseFloat(editModal.amount) < 0}
                className={`${GlobalStyles.buttonPrimary} flex-1 ${
                  !editModal.amount || parseFloat(editModal.amount) < 0
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Update Balance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoBalanceManagement;
